import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
// import moment from "moment";
import useFetch from "utils/hooks/useFetch";
// import Swal from "sweetalert2";

// Apis
import {
  GET_TICKET_BY_ID,
  GET_PRICE_CALCULATION,
  GET_ALL_PAYMENT_CHANNELS,
  CREATE_BOOKING,
  ticket_api,
  GET_CUSTOMER_DETAILS_BY_CUSTOMER_UUID,
} from "api";

// Images & Icons
import InvestmentFeeIcon from "assets/icons/InvestmentFeeIcon.png";
import LinkIcon from "assets/icons/LinkIcon.png";
import Method from "assets/icons/Method.png";
import LocationIcon from "pages/assets/icons/location";

// Components
// import NumberInput from "../components/QtyInput";
import ParticipantList from "../components/ParticipantList";
import PaymentMethods from "../components/PaymentMethods";
import PriceDetails from "../components/PriceDetails";
import { LoadingContext } from "components/layout/MainContainer";
import UserScheduleBooking from "../components/UserScheduleBooking";
import FormValidation from "../components/FormValidation";

// Helpers
import validateForm from "../helpers/validateForm";
import formatParticipants from "../helpers/formatParticipants";
import formatReferralCodes from "../helpers/formatReferralCodes";
import formatCurrency from "../helpers/formatCurrency";
import { convertTimeslotDateToObject } from "../helpers/convertTimeslotDateToObject";
import useToastMessage from "helpers/toast-message";

function WebinarDetails() {
  let autocompleteTimer;
  const { setIsLoading } = useContext(LoadingContext);

  const scheduleRef = useRef(null);

  const { showError } = useToastMessage();
  const { t } = useTranslation();
  const { id } = useParams();
  const { fetch: getTicket } = useFetch(GET_TICKET_BY_ID, false, ticket_api);

  const { data: priceCalculation, fetch: getPriceCalculation } = useFetch(
    GET_PRICE_CALCULATION,
    false,
    ticket_api,
  );
  const { data: paymentChannels, fetch: getAllPaymentChannels } = useFetch(
    GET_ALL_PAYMENT_CHANNELS,
    false,
    ticket_api,
  );
  const { fetch: createBooking } = useFetch(CREATE_BOOKING, false, ticket_api);
  const { fetch: getCustomerByCustomerUUID } = useFetch(
    GET_CUSTOMER_DETAILS_BY_CUSTOMER_UUID,
    false,
    ticket_api,
  );

  const [ticket, setTicket] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedPaymentMethod, selectPaymentMethod] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isRegistrationFormValid, setIsRegistrationFormValid] = useState(true);
  const [isPrivacyPolicyChecked, setIsPrivacyPolicyChecked] = useState(false);
  const [availableSchedules, setAvailableSchedules] = useState({});
  const [selectedSchedule, setSelectedSchedule] = useState([]);
  const [errorSelectedSchedule, setErrorSelectedSchedule] = useState(false);
  const [invalidEmailFormat, setInvalidEmailFormat] = useState(false);

  const handleBookingConfirm = (bookingData, selectedDate) => {
    if (bookingData) {
      let currentSelectedSchedule = selectedSchedule;
      if (currentSelectedSchedule.length > 0) {
        const exist = currentSelectedSchedule.find(
          (s) => s.id === bookingData.id,
        );
        if (!exist) {
          setSelectedSchedule([
            ...selectedSchedule,
            { ...bookingData, date: selectedDate },
          ]);
        } else {
          setSelectedSchedule(
            currentSelectedSchedule.filter((s) => s.id !== bookingData.id),
          );
        }
      } else {
        setSelectedSchedule([
          ...selectedSchedule,
          { ...bookingData, date: selectedDate },
        ]);
      }
    }
  };

  const handleFetchParticipantDetailsByCustomerID = ({ customerUUID }) => {
    getCustomerByCustomerUUID({ params: { customer_uuid: customerUUID } })
      .then((data) => {
        const { customer } = data || {};
        if (customer && customer.id) {
          setFormValues({
            ...formValues,
            [`company-name-0`]: customer.company_name,
            [`customer-division-0`]: customer.customer_division,
            [`customer-email-0`]: customer.customer_email,
            [`customer-name-0`]: customer.customer_name,
          });
        } else {
          setFormValues({
            ...formValues,
            [`company-name-0`]: "",
            [`customer-division-0`]: "",
            [`customer-email-0`]: "",
            [`customer-name-0`]: "",
          });
        }
      })
      .catch((err) => {
        showError(err?.data?.message);
      });
  };

  const validateRegistrationForm = () => {
    const requiredFields = [
      "customer-name",
      "customer-email",
      "phone-number",
      "company-name",
      "customer-division",
      "customer-phcode",
    ];
    if (
      ticket &&
      ticket.webinar_type &&
      ticket.webinar_type === "Consultation"
    ) {
      requiredFields.push("discussion_topic");
    }

    const result = validateForm(formValues, requiredFields);

    let isValidSchedule = false;
    if (
      selectedSchedule &&
      Array.isArray(selectedSchedule) &&
      selectedSchedule.length
    ) {
      isValidSchedule = true;
    }

    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    let isValidEmailFormat = false;

    if (
      formValues &&
      formValues["customer-email-0"] &&
      !emailRegex.test(formValues["customer-email-0"])
    ) {
      isValidEmailFormat = true;
    }

    setErrorSelectedSchedule(!isValidSchedule);
    setIsRegistrationFormValid(result);
    setInvalidEmailFormat(isValidEmailFormat);

    return result && isValidSchedule && isValidEmailFormat !== true;
  };

  const handleSubmit = () => {
    if (validateRegistrationForm()) {
      setIsLoading(true);
      const participants = formatParticipants(formValues, qty);
      createBooking({
        data: {
          ticket_id: id,
          mpayment_channel_id: selectedPaymentMethod,
          participants,
          schedules: selectedSchedule,
        },
      })
        .then((data) => {
          setIsLoading(false);
          const mRedirectUrl = data.m_redirect_url;
          window.location.replace(mRedirectUrl);
        })
        .catch((err) => {
          showError(
            err?.data?.message ||
              "We encountered an issue while processing your booking. Please try again in a moment.",
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    getTicket({ params: { ticket_id: id } })
      .then((res) => {
        const { ticket } = res || {};
        setTicket(ticket);
        setAvailableSchedules(
          ticket &&
            ticket.schedules &&
            Array.isArray(ticket.schedules) &&
            ticket.schedules.length > 0
            ? convertTimeslotDateToObject(ticket.schedules)
            : {},
        );
      })
      .catch((err) => {
        showError(err?.data?.message);
      });
    getAllPaymentChannels().catch((err) => {
      showError(err?.data?.message);
    });
  }, [id]);

  useEffect(() => {
    setIsPrivacyPolicyChecked(false);
  }, [qty, selectedPaymentMethod]);

  useEffect(() => {
    if (
      selectedPaymentMethod &&
      qty &&
      qty > 0 &&
      selectedSchedule &&
      Array.isArray(selectedSchedule) &&
      selectedSchedule.length > 0
    ) {
      // Get referral codes
      const referralCodes = formatReferralCodes(formValues);
      getPriceCalculation({
        data: {
          payment_channel_id: selectedPaymentMethod,
          qty: qty * selectedSchedule.length,
          referral_codes: referralCodes,
        },
        params: {
          id,
        },
      }).catch((err) => {
        showError(err?.data?.message);
      });
    }
  }, [selectedPaymentMethod, qty, selectedSchedule]);

  useEffect(() => {
    selectPaymentMethod(null);
  }, [formValues]);

  useEffect(() => {
    // check customer ID to server if any
    if (formValues && formValues[`customer_uuid-0`]) {
      if (autocompleteTimer) {
        clearTimeout(autocompleteTimer);
      }

      autocompleteTimer = setTimeout(
        () =>
          handleFetchParticipantDetailsByCustomerID({
            customerUUID: formValues[`customer_uuid-0`],
          }),
        1000,
      );
    }
  }, [formValues && formValues[`customer_uuid-0`]]);

  useEffect(() => {
    // Re-focus to schedule if there are invalid inputs
    if (errorSelectedSchedule) {
      scheduleRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [errorSelectedSchedule]);

  // const handleOpenLink = (link) => {
  //    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  // }

  return (
    <div>
      <section>
        <div className="relative">
          <img
            src={ticket?.thumbnail_url}
            alt="main"
            className="w-full max-h-40 sm:max-h-[500px] object-cover object-center"
          />
          {/* <div className="absolute top-0 bottom-0 left-0 right-0 text-white bg-black/60">
            <div className="flex flex-col items-center justify-center h-full gap-4 app-main-container sm:gap-8">
              <p className="max-w-md px-8 text-lg font-semibold text-center sm:text-3xl sm:font-bold app-animation-translate-start">
                {t("ticketDetails")}
              </p>
            </div>
          </div> */}
        </div>
      </section>
      <section>
        <div className="space-y-10 app-main-container md:space-y-12">
          <div className="text-xl font-bold capitalize md:text-5xl">
            {ticket && ticket.title}
          </div>
          <div className="space-y-4 md:space-y-6">
            <div className="flex items-center gap-4 ml-1">
              <img
                src={InvestmentFeeIcon}
                alt="academy"
                style={{ width: "40px", height: "auto" }}
              />
              <div className="ml-1 text-base md:text-xl">
                {formatCurrency(ticket && ticket.price)} / {t("session")}
              </div>
            </div>
            <div className="flex items-center gap-4 ml-3">
              <img
                src={Method}
                alt="academy"
                style={{ width: "30px", height: "auto" }}
              />
              <div className="ml-1 text-base capitalize md:text-xl">
                {ticket && ticket.type}
              </div>
            </div>
            {ticket &&
              ticket.type &&
              (ticket.type === "offline" || ticket.type === "hybrid") &&
              ticket.address_details && (
                <div className="flex gap-4">
                  <div>
                    <LocationIcon
                      bgFill={"#FF7B00"}
                      fill={"#FF7B00"}
                      size={50}
                    />
                  </div>
                  <div className="space-y-0.5 flex items-center">
                    <div className="text-base capitalize md:text-xl">
                      {ticket.address_details}
                    </div>
                  </div>
                </div>
              )}
          </div>
          <div>
            <div className="mb-4 text-base font-bold md:text-xl">
              {t("ticketDescription")}
            </div>
            <div
              className="text-base md:text-xl custom-list"
              dangerouslySetInnerHTML={{ __html: ticket && ticket.description }}
            ></div>
          </div>
          {ticket &&
          ticket.speakers &&
          Array.isArray(ticket.speakers) &&
          ticket.speakers.length > 0 ? (
            <div>
              <div className="mb-4 text-base font-bold md:text-xl">
                {t("webinarTicketSpeakersLabel")}
              </div>
              {ticket.speakers.map((speaker, index) => {
                const {
                  speaker_name,
                  speaker_profile_description,
                  speaker_profile_link,
                  speaker_profile_photo,
                } = speaker || {};
                return (
                  <div key={index} className="flex gap-8 py-4">
                    <div className="w-1/3">
                      <img
                        src={speaker_profile_photo}
                        alt="speaker_pp"
                        style={{
                          width: "300px",
                          height: "auto",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div className="flex flex-col w-4/5">
                      <div className="mb-2 text-xl font-semibold">
                        {speaker_name}
                      </div>
                      {speaker_profile_link ? (
                        <div className="flex items-center gap-4 mb-4">
                          <img
                            src={LinkIcon}
                            alt="speaker_link"
                            style={{ width: "15px", height: "auto" }}
                          />
                          <a
                            href={speaker_profile_link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {speaker_profile_link}
                          </a>
                        </div>
                      ) : null}
                      <div className="">{speaker_profile_description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {ticket && ticket.is_completed ? null : (
            <>
              {/* <div>
              <div className="mb-4 text-base font-bold md:text-xl">
                {t("qty")}
              </div>
              <NumberInput setQty={setQty} qty={qty} />
            </div> */}
              <div>
                <div className="mb-4 text-base font-bold md:text-xl">
                  {t("participantDetails")}
                </div>
                <ParticipantList
                  formValues={formValues}
                  setFormValues={setFormValues}
                  qty={qty}
                  isRegistrationFormValid={isRegistrationFormValid}
                  setIsRegistrationFormValid={setIsRegistrationFormValid}
                  ticket={ticket}
                  invalidEmailFormat={invalidEmailFormat}
                />
              </div>
              {qty && qty > 0 ? (
                <div ref={scheduleRef}>
                  <div className="mb-4 text-base font-bold md:text-xl">
                    {t("chooseSchedule")}
                  </div>
                  <UserScheduleBooking
                    availableSchedules={availableSchedules}
                    onScheduleSelect={handleBookingConfirm}
                    selectedSchedule={selectedSchedule}
                  />
                  <FormValidation
                    value={selectedSchedule || ""}
                    showError={errorSelectedSchedule}
                    errorMsg={"Please choose schedule"}
                  />
                </div>
              ) : null}
              {(qty && qty > 0 && (
                <div>
                  <div className="mb-4 text-base font-bold md:text-xl">
                    {t("choosePaymentMethod")}
                  </div>
                  <div className="w-full p-2 mb-4 text-xs leading-5 text-white bg-red-500 border border-2 border-red-700 rounded-lg sm:text-sm">
                    {t("qrisPaymentNote")}
                  </div>
                  <PaymentMethods
                    selectPaymentMethod={selectPaymentMethod}
                    selectedPaymentMethod={selectedPaymentMethod}
                    paymentMethods={paymentChannels}
                    priceDetails={priceCalculation}
                  />
                </div>
              )) ||
                null}
              {(qty &&
                qty > 0 &&
                selectedPaymentMethod &&
                priceCalculation &&
                selectedSchedule &&
                Array.isArray(selectedSchedule) &&
                selectedSchedule.length > 0 && (
                  <div>
                    <div className="mb-4 text-base font-bold md:text-xl">
                      {t("totalPrice")}
                    </div>
                    <PriceDetails priceDetails={priceCalculation} />
                    <div className="flex items-start">
                      <input
                        checked={isPrivacyPolicyChecked}
                        type="checkbox"
                        onChange={() =>
                          setIsPrivacyPolicyChecked(!isPrivacyPolicyChecked)
                        }
                        className="mt-1 mr-2 scale-125"
                      />
                      <span className="text-xs sm:text-sm">
                        {t("privacyPolicyAgreement")}
                        <a href="/privacy-policy" target="_blank">
                          <u>{t("privacyPolicy")}</u>
                        </a>
                        .
                      </span>
                    </div>
                  </div>
                )) ||
                null}
              {(qty && qty > 0 && selectedPaymentMethod && priceCalculation && (
                <div className="flex justify-center md:justify-end">
                  <button
                    onClick={handleSubmit}
                    className="px-12 py-2 text-sm text-white rounded-md bg-app-orange sm:text-base disabled:opacity-50"
                    disabled={isPrivacyPolicyChecked === false}
                  >
                    {t("continuePayment")}
                  </button>
                </div>
              )) ||
                null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default WebinarDetails;
